import React, { useEffect, useMemo, useState, createElement, Component } from 'react';
import { getTenant } from '../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { computeTransactionViewModel, computeDefaultFeeSummary, assertConsistency, getRowNetImpact, type LedgerRow, type Transaction } from '../../features/payments/utils/transactionViewModel';
import { buildScenario, DEFAULT_SCENARIO_ID, type TransactionScenario } from '../../features/payments/utils/transactionScenarios';
import { isDrillDownAvailable, exportRichness, feeDisplayName, ESTIMATED_FEE_EXPLAINER } from '../config/pricing';
interface Badge {
  tone?: 'emerald' | 'amber' | 'red' | 'slate' | 'blue' | 'violet';
  children: React.ReactNode;
}
function Badge({
  tone = 'slate',
  children
}: Badge) {
  const tones = {
    emerald: 'bg-[#FEE7E5] text-[#9F140F] ring-[#F1786F]',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    red: 'bg-red-50 text-red-700 ring-red-200',
    slate: 'bg-slate-50 text-slate-700 ring-slate-200',
    blue: 'bg-blue-50 text-blue-700 ring-blue-200',
    violet: 'bg-violet-50 text-violet-700 ring-violet-200'
  };
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${tones[tone] || tones.slate}`} data-id="element-15469" data-mp="3lxiaoncxc6io">
      {children}
    </span>;
}
interface MoneyProps {
  value: number;
  currency?: string;
}
function Money({
  value,
  currency = getTenant().currency.code
}: MoneyProps) {
  const abs = Math.abs(value);
  const formatted = `${abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`;
  return <span className="text-slate-900" data-id="element-15470" data-mp="2oa9vtb7fhvpn">
      {value < 0 ? `−${formatted}` : formatted}
    </span>;
}
function DeltaMoney({
  value,
  currency = getTenant().currency.code
}: MoneyProps) {
  const abs = Math.abs(value);
  const formatted = `${abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`;
  const isNeg = value < 0;
  return <span className={isNeg ? 'text-red-700' : 'text-[#9F140F]'} data-id="element-15471" data-mp="pwevosprlxj6">
      {isNeg ? `−${formatted}` : `+${formatted}`}
    </span>;
}
interface CardProps {
  title?: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}
function Card({
  title,
  subtitle,
  right,
  children
}: CardProps) {
  return <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200" data-id="element-15472" data-mp="18ozh5p34x0cy">
      {(title || subtitle || right) && <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-15473" data-mp="13pcyh04jerwr">
          <div data-id="element-15474" data-mp="yyedhn8lt5n6">
            {title && <div className="text-sm font-semibold text-slate-900" data-id="element-15475" data-mp="yxd37kzewn6i">
                {title}
              </div>}
            {subtitle && <div className="mt-1 text-xs text-slate-600" data-id="element-15476" data-mp="1rfqoym7h4ip8">{subtitle}</div>}
          </div>
          {right && <div className="shrink-0" data-id="element-15477" data-mp="1mnpff81ox62w">{right}</div>}
        </div>}
      <div className="px-5 py-4" data-id="element-15478" data-mp="1uxo6s4djpm3g">{children}</div>
    </div>;
}
interface RowProps {
  label: string;
  value: React.ReactNode;
  hint?: string;
}
function Row({
  label,
  value,
  hint
}: RowProps) {
  return <div className="flex items-start justify-between gap-4 py-2" data-id="element-15479" data-mp="23xjnspts8bbe">
      <div data-id="element-15480" data-mp="240flcd1pwxzb">
        <div className="text-sm text-slate-700" data-id="element-15481" data-mp="28x61t1hfpvl9">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-slate-500" data-id="element-15482" data-mp="28yh5l1avr3cd">{hint}</div>}
      </div>
      <div className="text-sm font-medium text-slate-900" data-id="element-15483" data-mp="2dvhb9b3ti42y">{value}</div>
    </div>;
}
interface TableProps {
  children: React.ReactNode;
}
function Table({
  children
}: TableProps) {
  return <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200" data-id="element-15484" data-mp="2mplyzlnnmze9">
      {children}
    </div>;
}
interface ThProps {
  children: React.ReactNode;
  align?: 'left' | 'right';
}
function Th({
  children,
  align = 'left'
}: ThProps) {
  return <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-600 ${align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-15485" data-mp="31llc5apmfpq9">
      {children}
    </th>;
}
interface TdProps {
  children: React.ReactNode;
  align?: 'left' | 'right';
}
function Td({
  children,
  align = 'left'
}: TdProps) {
  return <td className={`px-4 py-3 text-sm text-slate-800 ${align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-15486" data-mp="bwlzl9yr98xh">
      {children}
    </td>;
}
interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function Drawer({
  open,
  onClose,
  title,
  children
}: DrawerProps) {
  const {
    t
  } = useTranslation('common');
  if (!open) return null;
  return <div className="fixed inset-0 z-50" data-id="element-15487" data-mp="3og3kxzqc31w3">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} data-id="element-15488" data-mp="3ocy04woqmm66" />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl overflow-y-auto" data-id="element-15489" data-mp="3kbvy683frw0i">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4" data-id="element-15490" data-mp="13n078og6dp0p">
          <div className="text-sm font-semibold text-slate-900" data-id="element-15491" data-mp="zmyej4sf45xk">{title}</div>
          <button className="rounded-lg px-2 py-1 text-sm text-slate-600 hover:bg-slate-100" onClick={onClose} data-id="element-15492" data-mp="175pmzo3bh28d">
            {t('actions.close')}
          </button>
        </div>
        <div className="px-5 py-4" data-id="element-15493" data-mp="xa4aux4jalkw">{children}</div>
      </div>
    </div>;
}
function fmtDate(dateStr: string) {
  return dateStr || '—';
}
interface LedgerRow {
  id: string;
  posting: string;
  attemptId: string | null;
  type: 'payment' | 'fee' | 'refund' | 'dispute' | 'reserve_hold' | 'reserve_release' | 'dispute_reversal';
  gross: number | null;
  feeExTax: number | null;
  taxAmount: number | null;
  status: 'Posted' | 'Pending' | 'Scheduled';
  effectiveAt: string;
  settlement: {
    kind: 'payout' | 'reserve';
    id: string;
    date?: string;
    releaseDate?: string;
    status: string;
    amountInBucket: number;
  } | null;
  feeBreakdown: {
    jurisdiction: string;
    items: Array<{
      label: string;
      amountExTax: number;
      taxAmount: number;
    }>;
  } | null;
}
interface TransactionFinancialBreakdownProps {
  transactionId?: string;
  scenarioId?: string;
  currency?: string;
}
export function TransactionFinancialBreakdown({
  transactionId,
  scenarioId = DEFAULT_SCENARIO_ID,
  currency = getTenant().currency.code
}: TransactionFinancialBreakdownProps) {
  const {
    t
  } = useTranslation('common');
  const [taxDisplay, setTaxDisplay] = useState<'included' | 'separate'>('included');
  const asOf = new Date().toLocaleString('en-GB', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
  // Load scenario data (single source of truth)
  const scenario = useMemo(() => {
    try {
      return buildScenario(scenarioId);
    } catch (error) {
      console.error('Failed to load scenario:', scenarioId, error);
      // Return a fallback minimal scenario
      return {
        scenarioId: 'fallback',
        description: 'Fallback scenario',
        transaction: {
          id: '0',
          transactionId: transactionId || 'TXN-FALLBACK',
          amount: 0,
          currency: currency,
          date: new Date().toISOString()
        },
        ledgerRows: []
      };
    }
  }, [scenarioId, transactionId, currency]);
  const transaction = scenario.transaction;
  const ledger = scenario.ledgerRows || [];
  const [drawer, setDrawer] = useState<{
    open: boolean;
    ledgerId: string | null;
  }>({
    open: false,
    ledgerId: null
  });
  const selected = useMemo(() => ledger.find(x => x.id === drawer.ledgerId) || null, [drawer.ledgerId, ledger]);
  // Compute view model from ledger (single source of truth)
  const viewModel = useMemo(() => {
    try {
      const vm = computeTransactionViewModel(transaction, ledger, taxDisplay);
      // Ensure all required properties exist
      return {
        ...vm,
        timelineItems: vm.timelineItems || [],
        payoutBuckets: vm.payoutBuckets || [],
        reserveBucket: vm.reserveBucket || null,
        summary: vm.summary || {
          grossImpact: 0,
          feesPostedEx: 0,
          feesPendingEx: 0,
          taxPosted: 0,
          taxPending: 0,
          feesPostedDisplay: 0,
          feesPendingDisplay: 0,
          reserveHeld: 0,
          nextReserveRelease: null,
          netPosted: 0,
          paidOut: 0,
          scheduledPayoutTotal: 0
        }
      };
    } catch (error) {
      console.error('Failed to compute view model:', error);
      // Return a safe fallback
      return {
        headerAmount: 0,
        headerState: 'Unknown',
        summary: {
          grossImpact: 0,
          feesPostedEx: 0,
          feesPendingEx: 0,
          taxPosted: 0,
          taxPending: 0,
          feesPostedDisplay: 0,
          feesPendingDisplay: 0,
          reserveHeld: 0,
          nextReserveRelease: null,
          netPosted: 0,
          paidOut: 0,
          scheduledPayoutTotal: 0
        },
        payoutBuckets: [],
        reserveBucket: null,
        timelineItems: []
      };
    }
  }, [transaction, ledger, taxDisplay]);
  // Run consistency checks on mount and when data changes
  useEffect(() => {
    console.log(`\n=== Scenario: ${scenario.scenarioId} ===`);
    console.log(`Description: ${scenario.description}`);
    assertConsistency(viewModel, transaction, ledger);
  }, [viewModel, transaction, ledger, scenario]);
  const computed = viewModel.summary;
  // Clean default fee summary (single source of truth from pricing config)
  const feeSummary = useMemo(() => computeDefaultFeeSummary(transaction, ledger), [transaction, ledger]);
  const drillDownAvailable = isDrillDownAvailable();
  // CSV export — fixed Fee Ledger schema, keyed on fee_code
  function handleExportCsv() {
    const cols = ['fee_code', 'display_name', 'direction', 'owner', 'amount_ex_tax', 'tax', 'amount_inc_tax', 'currency', 'attribution_level', 'calc_status', 'scheme', 'card_product', 'domestic_or_crossborder', 'mcc_or_program', 'rule_id', 'effective_date', 'settlement_ref'];
    const esc = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const rows: string[][] = [];
    if (exportRichness() === 'totals_only') {
      // Basic visibility: totals only — one row per rolled-up summary line
      feeSummary.lines.forEach(l => {
        rows.push(['', l.label, 'debit', 'merchant', l.amountExTax.toFixed(2), '', l.amountExTax.toFixed(2), currency, 'summary', l.calcStatus, '', '', '', '', '', '', '']);
      });
    } else {
      // Full ledger: one row per fee component, keyed on fee_code
      ledger.filter(r => r.type === 'fee').forEach(r => {
        const items = r.feeBreakdown && r.feeBreakdown.items.length ? r.feeBreakdown.items : [{
          label: r.posting,
          amountExTax: r.feeExTax ?? 0,
          taxAmount: r.taxAmount ?? 0,
          feeCode: r.feeCode,
          calcStatus: r.calcStatus,
          rateDeterminants: undefined as any
        }];
        items.forEach((it: any) => {
          const code = it.feeCode ?? r.feeCode ?? '';
          const ex = Math.abs(it.amountExTax ?? 0);
          const tax = Math.abs(it.taxAmount ?? 0);
          const rd = it.rateDeterminants || {};
          rows.push([code, code ? feeDisplayName(code) : it.label, 'debit', 'merchant', ex.toFixed(2), tax.toFixed(2), (ex + tax).toFixed(2), currency, 'transaction', it.calcStatus ?? r.calcStatus ?? 'Actual', rd.scheme ?? '', rd.cardProduct ?? '', rd.domesticOrCrossBorder ?? '', rd.mccOrProgram ?? '', rd.ruleId ?? '', r.effectiveAt, r.settlement?.id ?? '']);
        });
      });
    }
    const csv = [cols, ...rows].map(r => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${transaction.transactionId}-fee-ledger.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
  const flags = [];
  if (Math.abs(computed.feesPendingEx) > 0.0001 || Math.abs(computed.taxPending) > 0.0001) flags.push({
    tone: 'amber' as const,
    label: 'Fees pending'
  });
  if (computed.reserveHeld > 0.0001) flags.push({
    tone: 'violet' as const,
    label: 'Reserve active'
  });
  if (viewModel.payoutBuckets.length > 0) flags.push({
    tone: 'blue' as const,
    label: 'Scheduled payouts'
  });
  // Helper functions for ledger table display
  function feeDisplay(row: LedgerRow) {
    if (row.type !== 'fee') return null;
    const ex = row.feeExTax ?? 0;
    const tax = row.taxAmount ?? 0;
    return taxDisplay === 'included' ? ex + tax : ex;
  }
  function taxDisplayAmount(row: LedgerRow) {
    if (row.type !== 'fee') return null;
    return row.taxAmount ?? 0;
  }
  function netImpact(row: LedgerRow) {
    if (row.type === 'fee') {
      const ex = row.feeExTax ?? 0;
      const tax = row.taxAmount ?? 0;
      return ex + tax;
    }
    if (row.type === 'payment' || row.type === 'refund' || row.type === 'dispute' || row.type === 'dispute_reversal') {
      return row.gross ?? 0;
    }
    if (row.type === 'reserve_hold') return -200;
    if (row.type === 'reserve_release') return 200;
    return 0;
  }
  return <div className="space-y-5" data-id="element-15494" data-mp="3homoklwhhl1i">
      {/* Recent activity timeline - moved to top */}
      <Card title={t('ui.TransactionFinancialBreakdown.attrs.recent_activity')} subtitle="Transaction timeline with all key events and amounts" data-id="element-15495" data-mp="3lqi00j84pet0">
        <div className="space-y-4" data-id="element-15496" data-mp="37x8u4sje3bf8">
          {viewModel.timelineItems && viewModel.timelineItems.length > 0 ? viewModel.timelineItems.map((t, idx) => {
          const isLast = idx === viewModel.timelineItems.length - 1;
          return <div key={idx} className="relative" data-id="element-15497" data-mp="3311lut3itnu4" data-mpi={idx}>
                  {!isLast && <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-slate-200" data-id="element-15498" data-mp="2p18viyvwp9mo" />}
                  <div className="flex gap-4" data-id="element-15499" data-mp="2u0lntz5jr8wn">
                    <div className="relative flex-shrink-0" data-id="element-15500" data-mp="2u1etewsrrsd7">
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center ring-4 ring-white ${t.type === 'positive' ? 'bg-[#FBD4D1]' : t.type === 'negative' ? 'bg-red-100' : 'bg-slate-100'}`} data-id="element-15501" data-mp="n9ni0u4umil8">
                        <div className={`h-2.5 w-2.5 rounded-full ${t.type === 'positive' ? 'bg-[#E2231A]' : t.type === 'negative' ? 'bg-red-600' : 'bg-slate-400'}`} data-id="element-15502" data-mp="xdkqh0qb0tp9" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pb-4" data-id="element-15503" data-mp="16bwdytvvjbdh">
                      <div className="flex items-start justify-between gap-3" data-id="element-15504" data-mp="1l1c0tysaawnw">
                        <div className="flex-1" data-id="element-15505" data-mp="1g4bvjq39dxxu">
                          <div className="text-sm font-medium text-slate-900" data-id="element-15506" data-mp="1g5mwzj98etls">
                            {t.label}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600" data-id="element-15507" data-mp="1q1glm1086djx">
                            {t.at}
                          </div>
                        </div>
                        {t.amount !== null && <div className="flex-shrink-0" data-id="element-15508" data-mp="3ruege2dkbg7i">
                            <div className={`text-sm font-semibold ${t.type === 'positive' ? 'text-[#9F140F]' : t.type === 'negative' ? 'text-red-700' : 'text-slate-700'}`} data-id="element-15509" data-mp="1usp54dhv11iq">
                              {t.type === 'positive' ? '+' : t.type === 'negative' ? '−' : ''}
                              <Money value={t.amount} currency={currency} data-id="element-15510" data-mp="2dr1g84sc0lgy" />
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>;
        }) : <div className="text-sm text-slate-600" data-id="element-15511" data-mp="2siaair76iv0f">
              {t('ui.TransactionFinancialBreakdown.no_activity_recorded')}
            </div>}
        </div>
      </Card>

      {/* Clean default fee summary — same for ALL merchants */}
      <Card title="Fee summary" subtitle="What you paid to process this payment." data-id="element-15512" data-mp="ptsk629zqtml">
        <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-15513" data-mp="lrp2ux0n4ute">
          <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-15514" data-mp="gslxfbgr0mkw">
            <div data-id="element-15515" data-mp="guozitv6e65z">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-15516" data-mp="bu0x7c1hy2wg">
                Total processing cost
              </div>
              <div className="mt-1 text-2xl font-semibold text-slate-900" data-id="element-15517" data-mp="19h0044is872s">
                <Money value={feeSummary.totalCostExTax} currency={currency} data-id="element-15518" data-mp="14jhmi0ttl3og" />
              </div>
            </div>
            <div className="text-right" data-id="element-15519" data-mp="zqf0cdrdgwia">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-15520" data-mp="usn0mxkhk63j">
                Effective rate
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900" data-id="element-15521" data-mp="1s92hji38zre3">
                {feeSummary.effectiveRatePct.toFixed(2)}% ·{' '}
                {Math.round(feeSummary.effectiveRateBps)} bps
              </div>
            </div>
          </div>

          <div className="mt-4 divide-y divide-slate-200" data-id="element-15522" data-mp="10kfyx6hdwnpy">
            {feeSummary.lines.map(line => <div key={line.key} className="flex items-center justify-between gap-3 py-2" data-id="element-15523" data-mp="mtb7gro671qp">
                <div className="flex items-center gap-2" data-id="element-15524" data-mp="qvfz5kv25oap">
                  <span className="text-sm text-slate-700" data-id="element-15525" data-mp="1ekgzektqlt4w">{line.label}</span>
                  {line.calcStatus === 'Estimated' && <Badge tone="amber" data-id="element-15526" data-mp="1ji8zw38g6nkp">Estimated</Badge>}
                  {line.calcStatus === 'Reconciled' && <Badge tone="blue" data-id="element-15527" data-mp="15ln5x3bpdivk">Reconciled</Badge>}
                </div>
                <div className="text-sm font-medium text-slate-900" data-id="element-15528" data-mp="1akqeuyut098t">
                  <Money value={line.amountExTax} currency={currency} data-id="element-15529" data-mp="3q8ysff9426ke" />
                </div>
              </div>)}
          </div>

          {feeSummary.hasEstimated && <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200" data-id="element-15530" data-mp="3mr2m5ocbbwyk">
              {ESTIMATED_FEE_EXPLAINER}
            </div>}
        </div>
      </Card>

      {/* Financial summary */}
      <Card title={t('ui.TransactionFinancialBreakdown.attrs.financial_summary')} subtitle={`As of ${asOf}. Values reflect posted ledger entries; pending rows may change totals later.`} right={<div className="flex flex-wrap justify-end gap-2" data-id="element-15532" data-mp="2z29ptqrs3n3f">
            {flags.map(f => <Badge key={f.label} tone={f.tone} data-id="element-15533" data-mp="2z0f62fs8u10x">
                {f.label}
              </Badge>)}
          </div>} data-id="element-15531" data-mp="jum91k5wi9x">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-15534" data-mp="38zedjv19vqmc">
          {/* Balance impact */}
          <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-15535" data-mp="2l9thcag3ywv4">
            <div className="flex items-center justify-between gap-3" data-id="element-15536" data-mp="2gca4nez26s5a">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-15537" data-mp="2g9xon7cv2ruv">
                {t('ui.TransactionFinancialBreakdown.impact_on_merchant_balance')}
              </div>
              <div className="flex items-center gap-2" data-id="element-15538" data-mp="1mhx9q251i38t">
                <span className="text-xs text-slate-600" data-id="element-15539" data-mp="1mji3vqnfzk65">
                  {t('ui.TransactionFinancialBreakdown.tax_display')}
                </span>
                <div className="inline-flex rounded-full bg-white p-1 ring-1 ring-slate-200" data-id="element-15540" data-mp="1vhai0rhfglry">
                  <button onClick={() => setTaxDisplay('included')} className={`rounded-full px-3 py-1 text-xs font-medium ${taxDisplay === 'included' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-15541" data-mp="1qjs7wxroc0id">
                    {t('ui.TransactionFinancialBreakdown.included')}
                  </button>
                  <button onClick={() => setTaxDisplay('separate')} className={`rounded-full px-3 py-1 text-xs font-medium ${taxDisplay === 'separate' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-15542" data-mp="635gjn02kryg">
                    {t('ui.TransactionFinancialBreakdown.separate')}
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-3 divide-y divide-slate-200" data-id="element-15543" data-mp="2cyqubzne1200">
              <Row label={t('ui.TransactionFinancialBreakdown.attrs.gross_impact')} hint="Customer-facing amounts (payments/refunds/disputes)." value={<DeltaMoney value={computed.grossImpact} currency={currency} data-id="element-15545" data-mp="1p1tm4hsynehk" />} data-id="element-15544" data-mp="1z00z6pk4pcea" />

              {taxDisplay === 'included' ? <>
                  <Row label={t('ui.TransactionFinancialBreakdown.attrs.fees_posted_incl_tax_where_applicable')} hint="Includes tax for taxable fee components." value={<DeltaMoney value={computed.feesPostedDisplay} currency={currency} data-id="element-15547" data-mp="3hy5vst5ckkvi" />} data-id="element-15546" data-mp="3jgw1vns7ji7a" />
                </> : <>
                  <Row label={t('ui.TransactionFinancialBreakdown.attrs.fees_posted_ex_tax')} hint="Fee amounts excluding tax." value={<DeltaMoney value={computed.feesPostedEx} currency={currency} data-id="element-15549" data-mp="3vxos8oosq7rz" />} data-id="element-15548" data-mp="382m4yl5n4cdm" />

                  <Row label={t('ui.TransactionFinancialBreakdown.attrs.tax_on_fees_posted')} hint="Tax booked on fee components (some fee types may have 0 tax)." value={<DeltaMoney value={computed.taxPosted} currency={currency} data-id="element-15551" data-mp="zswaiaae4uoj" />} data-id="element-15550" data-mp="s9wsa7yo196r" />
                </>}
              <Row label={t('ui.TransactionFinancialBreakdown.attrs.reserve_held')} hint={computed.nextReserveRelease ? `Scheduled release on ${computed.nextReserveRelease}.` : 'No reserve active.'} value={computed.reserveHeld > 0.0001 ? <DeltaMoney value={-computed.reserveHeld} currency={currency} data-id="element-15553" data-mp="1si3bdfxbhwcy" /> : <span className="text-sm text-slate-500" data-id="element-15554" data-mp="1xetqo11asnxb">—</span>} data-id="element-15552" data-mp="19rc4cl0syi1q" />

              <Row label={t('ui.TransactionFinancialBreakdown.attrs.net_impact_posted_so_far')} hint="Sum of posted ledger entries (truth)." value={<DeltaMoney value={computed.netPosted} currency={currency} data-id="element-15556" data-mp="38vmn6ki61xv0" />} data-id="element-15555" data-mp="2z6uziok51zpw" />
            </div>
          </div>

          {/* Cash movement */}
          <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-15557" data-mp="2g7h03r3n3ws0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-15558" data-mp="2b95trqck04ej">
              {t('ui.TransactionFinancialBreakdown.cash_movement')}
            </div>
            <div className="mt-3 divide-y divide-slate-200" data-id="element-15559" data-mp="2p6ubwqf8kxfw">
              <Row label={t('ui.TransactionFinancialBreakdown.attrs.paid_out')} hint="Amount already included in completed payouts." value={<DeltaMoney value={computed.paidOut} currency={currency} data-id="element-15561" data-mp="g2xfvgpexkp" />} data-id="element-15560" data-mp="2l6k6pfz9qb23" />

              <Row label={t('ui.TransactionFinancialBreakdown.attrs.scheduled_in_upcoming_payouts')} hint="Sum of amounts mapped to scheduled payout buckets." value={<DeltaMoney value={computed.scheduledPayoutTotal} currency={currency} data-id="element-15563" data-mp="31kbslt9qu1c" />} data-id="element-15562" data-mp="7zubn9pxuita" />

              <Row label={t('ui.TransactionFinancialBreakdown.attrs.held_in_reserve')} hint="Funds temporarily withheld per reserve policy." value={computed.reserveHeld > 0.0001 ? <DeltaMoney value={-computed.reserveHeld} currency={currency} data-id="element-15565" data-mp="lybvgwcq3b5x" /> : <span className="text-sm text-slate-500" data-id="element-15566" data-mp="1ehhf5f1utgi3">—</span>} data-id="element-15564" data-mp="qthenneqqbqb" />

              <Row label={t('ui.TransactionFinancialBreakdown.attrs.next_scheduled_payout')} hint="Where the next part of this transaction will land." value={viewModel.payoutBuckets.length ? <span className="text-sm text-slate-900" data-id="element-15568" data-mp="1acn8x2ap61ir">
                      {viewModel.payoutBuckets[0].id} •{' '}
                      {viewModel.payoutBuckets[0].date}
                    </span> : <span className="text-sm text-slate-500" data-id="element-15569" data-mp="vo0oim67qjko">—</span>} data-id="element-15567" data-mp="1obwr27igr5pd" />
            </div>
          </div>
        </div>
      </Card>

      {/* Unified ledger table */}
      <Table data-id="element-15570" data-mp="mnvpp0f34ti4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-15571" data-mp="mnlzpcu1imcf">
          <div data-id="element-15572" data-mp="hpkfuposiizw">
            <div className="text-sm font-semibold text-slate-900" data-id="element-15573" data-mp="horfc6gfcpq7">
              {t('ui.TransactionFinancialBreakdown.ledger_for_this_transaction')}
            </div>
            <div className="mt-1 text-xs text-slate-600" data-id="element-15574" data-mp="2y8uup2jujawg">
              {t('ui.TransactionFinancialBreakdown.every_posting_that_affects_the_merchant')}
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-15575" data-mp="3799qwwycmaeq">
            <button onClick={handleExportCsv} className="rounded-xl bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50" data-id="element-15576" data-mp="2am6a293gd4c5">
              {t('ui.TransactionFinancialBreakdown.export_csv')}
            </button>
          </div>
        </div>
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3" data-id="element-15577" data-mp="2odt5k6pkm8tv">
          <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-15578" data-mp="1rtkelpwgjfs3">
            <div className="text-xs text-slate-600" data-id="element-15579" data-mp="1vvpbcxi7csiy">
              {t('ui.TransactionFinancialBreakdown.fee_tax_display')}{' '}
              <span className="font-semibold text-slate-900" data-id="element-15580" data-mp="p9oacrvikti9">
                {taxDisplay === 'included' ? 'Included in fee' : 'Shown separately'}
              </span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-15581" data-mp="u7zk72lt740h">
          <table className="min-w-full" data-id="element-15582" data-mp="u76iwh5md6pq">
            <thead className="bg-white" data-id="element-15583" data-mp="uac7lvac66kx">
              <tr className="border-b border-slate-200" data-id="element-15584" data-mp="186z8ua9bniwn">
                <Th data-id="element-15585" data-mp="185ediif6nvxq">{t('ui.TransactionFinancialBreakdown.posting')}</Th>
                <Th align="right" data-id="element-15586" data-mp="138e3jwf7q91c">
                  {t('ui.TransactionFinancialBreakdown.gross')}
                </Th>
                <Th align="right" data-id="element-15587" data-mp="1hzeo2zt8htdu">{t('labels.fee')}</Th>
                {taxDisplay === 'separate' && <Th align="right" data-id="element-15588" data-mp="1d0ton2lt3dqg">{t('labels.tax')}</Th>}
                <Th align="right" data-id="element-15589" data-mp="1qyi600go02qr">
                  {t('ui.TransactionFinancialBreakdown.net_impact')}
                </Th>
                <Th data-id="element-15590" data-mp="25kxq18nhzr5p">{t('labels.status')}</Th>
                <Th data-id="element-15591" data-mp="25m0gjfgwrrl3">{t('ui.TransactionFinancialBreakdown.effective')}</Th>
                <Th data-id="element-15592" data-mp="2akk3nj78n9f1">{t('ui.TransactionFinancialBreakdown.settlement')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-15593" data-mp="1wl272hnparv4">
              {ledger.map(r => {
              const feeShown = feeDisplay(r);
              const taxShown = taxDisplayAmount(r);
              const net = getRowNetImpact(r);
              const settlement = r.settlement;
              const settlementUi = settlement?.kind === 'payout' ? <div className="space-y-1" data-id="element-15594" data-mp="1dub3xxub3xp6">
                      <div className="text-sm font-medium text-slate-900" data-id="element-15595" data-mp="1dvm7bwjub3fr">
                        {settlement.id}
                      </div>
                      <div className="text-xs text-slate-600" data-id="element-15596" data-mp="13vvd7vz6yqg7">
                        {settlement.date} • {settlement.status}
                      </div>
                    </div> : settlement?.kind === 'reserve' ? <div className="space-y-1" data-id="element-15597" data-mp="1682k0e6b8myf">
                      <div className="text-sm font-medium text-slate-900" data-id="element-15598" data-mp="1b5ucb9avab35">
                        {t('ui.settlementUi.reserve')} {settlement.id}
                      </div>
                      <div className="text-xs text-slate-600" data-id="element-15599" data-mp="1f64gqhj0gtfw">
                        {t('ui.settlementUi.releases')}{' '}
                        {fmtDate(settlement.releaseDate || '')}
                      </div>
                    </div> : <span className="text-slate-500" data-id="element-15600" data-mp="1u3oh1b4yq0fq">
                      {t('ui.settlementUi.not_yet_included')}
                    </span>;
              return <tr key={r.id} className="hover:bg-slate-50" data-id="element-15601" data-mp="22x9vetd7tfom">
                    <Td data-id="element-15602" data-mp="22yupkhvmawly">
                      <div className="flex items-center gap-2" data-id="element-15603" data-mp="27wmt695mk75z">
                        <span className="font-medium text-slate-900" data-id="element-15604" data-mp="27u0dtnchsnd7">
                          {r.type === 'fee' && r.feeCode ? feeDisplayName(r.feeCode) : r.posting}
                        </span>
                        {r.type === 'fee' && r.calcStatus === 'Estimated' && <Badge tone="amber" data-id="element-15605" data-mp="2d0f7hmx91p1b">Estimated</Badge>}
                        {r.type === 'fee' && r.calcStatus === 'Reconciled' && <Badge tone="blue" data-id="element-15606" data-mp="291px837icnlw">Reconciled</Badge>}
                        {r.type === 'fee' && r.calcStatus === 'Actual' && <Badge tone="slate" data-id="element-15607" data-mp="30r2rh6kfw548">Actual</Badge>}
                      </div>
                      <div className="mt-1 text-xs text-slate-500" data-id="element-15608" data-mp="2vxzzh2vb93q5">{r.id}</div>
                    </Td>
                    <Td align="right" data-id="element-15609" data-mp="2rw4xdw5k2mbb">
                      {r.gross == null ? <span className="text-slate-500" data-id="element-15610" data-mp="3ohonu0l300ye">—</span> : <DeltaMoney value={r.gross} currency={currency} data-id="element-15611" data-mp="3jnu8tagf8a5n" />}
                    </Td>
                    <Td align="right" data-id="element-15612" data-mp="1hty447wrm3pj">
                      {r.type !== 'fee' ? <span className="text-slate-500" data-id="element-15613" data-mp="1mq6e76w3v6pu">—</span> : <div className="flex items-center justify-end gap-2" data-id="element-15614" data-mp="181ikz79qf932">
                          <DeltaMoney value={feeShown!} currency={currency} data-id="element-15615" data-mp="1d03d0jetymgf" />
                          {r.feeBreakdown && drillDownAvailable && <button onClick={() => setDrawer({
                      open: true,
                      ledgerId: r.id
                    })} className="text-xs text-blue-600 hover:text-blue-700 hover:underline" title={t('ui.TransactionFinancialBreakdown.attrs.view_fee_breakdown')} data-id="element-15616" data-mp="z3q4hzs9xve3">
                              {t('ui.TransactionFinancialBreakdown.details')}
                            </button>}
                        </div>}
                    </Td>
                    {taxDisplay === 'separate' && <Td align="right" data-id="element-15617" data-mp="hr9h5xayp8dc">
                        {r.type !== 'fee' ? <span className="text-slate-500" data-id="element-15618" data-mp="viemng10guvl">—</span> : taxShown === 0 ? <span className="text-slate-500" data-id="element-15619" data-mp="rh2t4zf0npi4">0.00</span> : <DeltaMoney value={taxShown!} currency={currency} data-id="element-15620" data-mp="15garxr79ovls" />}
                      </Td>}
                    <Td align="right" data-id="element-15621" data-mp="378yya9f7jz5s">
                      <DeltaMoney value={net} currency={currency} data-id="element-15622" data-mp="379rzyvzb7yh2" />
                    </Td>
                    <Td data-id="element-15623" data-mp="32axko40jws1p">
                      {r.status === 'Posted' ? <Badge tone="emerald" data-id="element-15624" data-mp="33rks5q870nx5">
                          {t('ui.TransactionFinancialBreakdown.posted')}
                        </Badge> : r.status === 'Pending' ? <Badge tone="amber" data-id="element-15625" data-mp="3dopje4gwb9kt">{t('status.pending')}</Badge> : <Badge tone="blue" data-id="element-15626" data-mp="3iov9hg7ioo0s">
                          {t('ui.TransactionFinancialBreakdown.scheduled')}
                        </Badge>}
                    </Td>
                    <Td data-id="element-15627" data-mp="2pz4t10iexa1i">
                      <div className="text-sm text-slate-800" data-id="element-15628" data-mp="2q0g0pbezb1xw">
                        {r.effectiveAt}
                      </div>
                    </Td>
                    <Td data-id="element-15629" data-mp="2zp7nl556mw1y">{settlementUi}</Td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Table>

      {/* Payout / transfer story */}
      <Card title={t('ui.TransactionFinancialBreakdown.attrs.payout_transfer_story')} subtitle="Explains where money lands: payouts vs reserve, and how asynchronous fees/taxes affect reconciliation." data-id="element-15630" data-mp="278wyh6ss3rwx">
        <div className="space-y-3" data-id="element-15631" data-mp="kdrz7jfmh2tr">
          {viewModel.payoutBuckets && viewModel.payoutBuckets.length > 0 ? viewModel.payoutBuckets.map(p => <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-15632" data-mp="aesrq46lfd8c">
                <div data-id="element-15633" data-mp="opzi67aq3b2">
                  <div className="text-sm font-semibold text-slate-900" data-id="element-15634" data-mp="nexobxev5ml">
                    {p.id}
                  </div>
                  <div className="mt-1 text-xs text-slate-600" data-id="element-15635" data-mp="3sss68pjm1aoz">
                    {p.date} • {p.status} • {p.rows.length}{' '}
                    {t('ui.TransactionFinancialBreakdown.postings')}
                  </div>
                </div>
                <div className="text-sm font-semibold" data-id="element-15636" data-mp="3dv9m60hvnlnv">
                  <DeltaMoney value={p.total} currency={currency} data-id="element-15637" data-mp="3dvjaljn9xkrg" />
                </div>
              </div>) : <div className="text-sm text-slate-600" data-id="element-15638" data-mp="396gyksoe07gn">
              {t('ui.TransactionFinancialBreakdown.no_scheduled_payouts')}
            </div>}
          {viewModel.reserveBucket && <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-violet-50 p-4 ring-1 ring-violet-200" data-id="element-15639" data-mp="2z8azeitytffh">
              <div data-id="element-15640" data-mp="3mxlsde52q5j2">
                <div className="text-sm font-semibold text-violet-900" data-id="element-15641" data-mp="3myf0ojjol33b">
                  {t('ui.settlementUi.reserve')} {viewModel.reserveBucket.id}
                </div>
                <div className="mt-1 text-xs text-violet-900/80" data-id="element-15642" data-mp="3e6d10nhfrqu3">
                  {t('ui.TransactionFinancialBreakdown.held_now_releases')}{' '}
                  {viewModel.reserveBucket.releaseAt || '—'}
                </div>
              </div>
              <div className="text-sm font-semibold text-violet-900" data-id="element-15643" data-mp="9p8x3z0bjsvm">
                <DeltaMoney value={-viewModel.reserveBucket.heldNow} currency={currency} data-id="element-15644" data-mp="eiva5sae9yry" />
              </div>
            </div>}
        </div>
      </Card>

      {/* Fee breakdown drawer */}
      <Drawer open={drawer.open} onClose={() => setDrawer({
      open: false,
      ledgerId: null
    })} title={selected ? `Fee breakdown — ${selected.posting}` : 'Fee breakdown'} data-id="element-15645" data-mp="28yaf3jltpi9x">
        {!selected || !selected.feeBreakdown ? <div className="text-sm text-slate-600" data-id="element-15646" data-mp="1bbbj7b2yged3">
            {t('ui.TransactionFinancialBreakdown.no_fee_details_available')}
          </div> : <div className="space-y-4" data-id="element-15647" data-mp="1q5g1ou3uly7t">
            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-15648" data-mp="1l8g5dawsw9tw">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-15649" data-mp="31mpkdef5km82">
                {t('ui.TransactionFinancialBreakdown.jurisdiction')}
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-900" data-id="element-15650" data-mp="2wq7eucc00juo">
                {selected.feeBreakdown.jurisdiction}
              </div>
              <div className="mt-2 text-xs text-slate-600" data-id="element-15651" data-mp="z1c1bvqq7wp4">
                {t('ui.TransactionFinancialBreakdown.display_mode')}{' '}
                <span className="font-semibold text-slate-900" data-id="element-15652" data-mp="13xalt88xouk9">
                  {taxDisplay === 'included' ? 'Tax included in fee' : 'Tax shown separately'}
                </span>
              </div>
            </div>

            <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200" data-id="element-15653" data-mp="1hpqsqk4yhcqg">
              <div className="text-sm font-semibold text-slate-900" data-id="element-15654" data-mp="1mq4tdh489keg">
                {t('ui.TransactionFinancialBreakdown.components')}
              </div>
              <div className="mt-3 divide-y divide-slate-200" data-id="element-15655" data-mp="1ro6j2kw5shyw">
                {selected.feeBreakdown.items.map((it, idx) => {
              const rd = it.rateDeterminants;
              const determinants = rd ? [['Scheme', rd.scheme], ['Card product', rd.cardProduct], ['Region', rd.domesticOrCrossBorder], ['MCC / Program', rd.mccOrProgram], ['Rule', rd.ruleId]].filter(([, v]) => !!v) : [];
              return <div key={idx} className="py-2" data-id="element-15656" data-mp="3762uyxnvn23m" data-mpi={idx}>
                      <div className="grid grid-cols-3 items-center gap-3" data-id="element-15657" data-mp="8oywqcpctd3c">
                        <div className="col-span-1 flex items-center gap-2 text-sm text-slate-700" data-id="element-15658" data-mp="3qnjrt3rimgw">
                          <span data-id="element-15659" data-mp="3uwp28mq24ick">
                            {it.feeCode ? feeDisplayName(it.feeCode) : it.label}
                          </span>
                          {it.calcStatus === 'Estimated' && <Badge tone="amber" data-id="element-15660" data-mp="2jje77vc6sff3">Estimated</Badge>}
                          {it.calcStatus === 'Reconciled' && <Badge tone="blue" data-id="element-15661" data-mp="26gqcgm3rrx5x">Reconciled</Badge>}
                        </div>
                        <div className="col-span-1 text-right text-sm font-medium" data-id="element-15662" data-mp="21h5dwcsihqtq">
                          <DeltaMoney value={it.amountExTax} currency={currency} data-id="element-15663" data-mp="1wikeugozxd6v" />
                        </div>
                        <div className="col-span-1 text-right text-sm font-medium" data-id="element-15664" data-mp="1nhvvr7kqwmn4">
                          {it.taxAmount === 0 ? <span className="text-slate-500" data-id="element-15665" data-mp="1iq4gvuayc5ct">0.00</span> : <DeltaMoney value={it.taxAmount} currency={currency} data-id="element-15666" data-mp="1dt499eygeujl" />}
                        </div>
                        <div className="col-span-3 -mt-1 text-xs text-slate-500" data-id="element-15667" data-mp="14ql76x2hslye">
                          {t('ui.TransactionFinancialBreakdown.ex_tax_tax')}
                        </div>
                      </div>
                      {determinants.length > 0 && <div className="mt-2 rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200" data-id="element-15668" data-mp="3gdoskgmc9utw">
                          <div className="text-xs font-semibold text-slate-700" data-id="element-15669" data-mp="3khed8uzw7s9n">
                            Why this rate
                          </div>
                          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1" data-id="element-15670" data-mp="3pd2033qwk9bz">
                            {determinants.map(([label, value]) => <div key={label as string} className="flex items-center justify-between gap-2" data-id="element-15671" data-mp="2wrk16fnee7qg">
                                <dt className="text-xs text-slate-500" data-id="element-15672" data-mp="36hbwjc3bn4vz">
                                  {label}
                                </dt>
                                <dd className="text-xs font-medium text-slate-800" data-id="element-15673" data-mp="2dzzmmhk3nzw1">
                                  {value as string}
                                </dd>
                              </div>)}
                          </dl>
                        </div>}
                    </div>;
            })}
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-id="element-15674" data-mp="3m1n39tyca4mh">
              <div className="flex items-center justify-between" data-id="element-15675" data-mp="2yc15naxm7xgl">
                <div className="text-sm font-semibold text-slate-900" data-id="element-15676" data-mp="2ycklmm3pgbrz">
                  {t('ui.TransactionFinancialBreakdown.totals')}
                </div>
                <Badge tone={selected.status === 'Posted' ? 'emerald' : selected.status === 'Pending' ? 'amber' : 'blue'} data-id="element-15677" data-mp="37d901hx0ge5e">
                  {selected.status}
                </Badge>
              </div>
              <div className="mt-3 divide-y divide-slate-200" data-id="element-15678" data-mp="q4pchqnn2o7i">
                <Row label={t('ui.TransactionFinancialBreakdown.attrs.fee_ex_tax')} value={<DeltaMoney value={selected.feeExTax ?? 0} currency={currency} data-id="element-15680" data-mp="g6hwpb536c73" />} data-id="element-15679" data-mp="q3e4tfr2owb4" />

                <Row label={t('ui.TransactionFinancialBreakdown.attrs.tax_on_fee')} value={<DeltaMoney value={selected.taxAmount ?? 0} currency={currency} data-id="element-15682" data-mp="1durz3r8sb1yq" />} data-id="element-15681" data-mp="l7fo8ebzkil9" />

                <Row label={t('ui.TransactionFinancialBreakdown.attrs.total_fee_inc_tax')} value={<DeltaMoney value={(selected.feeExTax ?? 0) + (selected.taxAmount ?? 0)} currency={currency} data-id="element-15684" data-mp="1rs86b4ywmzrj" />} data-id="element-15683" data-mp="144738bkhwbji" />
              </div>
            </div>
          </div>}
      </Drawer>
    </div>;
}